'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';
import { useAnalytics } from '@/core/analytics';

export default function AboutPage() {
	const analytics = useAnalytics();

	useEffect(() => {
		analytics.logEvent('about_page_visited');
	}, []);

	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto text-center">
						<h1 className="text-5xl md:text-7xl font-bold mb-6">
							About
							<br />
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								TabStudio
							</span>
						</h1>
						<p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
							We&apos;re building the future of AI-powered video
							creation. Empowering creators with cutting-edge
							technology.
						</p>
					</div>
				</section>

				{/* Mission Section */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto max-w-4xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
							Our Mission
						</h2>
						<p className="text-lg text-muted-foreground leading-relaxed mb-6">
							At TabSpace, we believe that everyone should have
							access to professional video creation tools. Our
							mission is to democratize video production through
							artificial intelligence, making it accessible,
							affordable, and incredibly powerful.
						</p>
						<p className="text-lg text-muted-foreground leading-relaxed">
							We&apos;re committed to pushing the boundaries of
							what&apos;s possible with AI whilst maintaining the
							highest standards of quality, ethics, and user
							experience. Our platform empowers creators,
							businesses, and storytellers to bring their visions
							to life without the need for expensive equipment or
							technical expertise.
						</p>
					</div>
				</section>

				{/* Values Section */}
				<section className="py-16 px-4">
					<div className="container mx-auto">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Our Values
						</h2>
						<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M13 10V3L4 14h7v7l9-11h-7z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Innovation
								</h3>
								<p className="text-muted-foreground">
									We constantly push boundaries and explore
									new technologies to deliver cutting-edge AI
									video generation tools.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary to-secondary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									User-Centric
								</h3>
								<p className="text-muted-foreground">
									Our users are at the heart of everything we
									do. We build tools that are intuitive,
									powerful, and delightful to use.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Ethical AI
								</h3>
								<p className="text-muted-foreground">
									We&apos;re committed to responsible AI
									development, ensuring our technology is used
									ethically and safely.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary-dark to-secondary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Global Reach
								</h3>
								<p className="text-muted-foreground">
									We&apos;re building a platform for creators
									worldwide, breaking down barriers of
									geography and language.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary-dark to-accent rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Speed
								</h3>
								<p className="text-muted-foreground">
									We value your time. Our platform is
									optimised for fast generation and iteration,
									so you can create more in less time.
								</p>
							</div>

							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-semibold mb-3">
									Transparency
								</h3>
								<p className="text-muted-foreground">
									We believe in being open and honest with our
									users about how our AI works and what to
									expect.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* Team Section */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto max-w-4xl">
						<h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
							Our Team
						</h2>
						<p className="text-lg text-muted-foreground text-center leading-relaxed mb-12">
							TabStudio is built by a passionate team of
							engineers, designers, and AI researchers from around
							the world. We&apos;re united by our vision to make
							professional video creation accessible to everyone.
						</p>
						<div className="text-center">
							<Link href="/careers" prefetch={false}>
								<Button
									variant="outline"
									size="lg"
									className="glass-light glass-hover border-2 border-white/30 text-white font-semibold"
								>
									Join Our Team
								</Button>
							</Link>
						</div>
					</div>
				</section>

				{/* Stats Section */}
				<section className="py-16 px-4">
					<div className="container mx-auto">
						<div className="grid gap-8 md:grid-cols-4 max-w-6xl mx-auto">
							<div className="text-center p-6">
								<div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
									50K+
								</div>
								<p className="text-muted-foreground">
									Active Users
								</p>
							</div>
							<div className="text-center p-6">
								<div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-secondary to-secondary-dark bg-clip-text text-transparent">
									1M+
								</div>
								<p className="text-muted-foreground">
									Videos Generated
								</p>
							</div>
							<div className="text-center p-6">
								<div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-accent to-accent-dark bg-clip-text text-transparent">
									150+
								</div>
								<p className="text-muted-foreground">
									Countries
								</p>
							</div>
							<div className="text-center p-6">
								<div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-primary-dark to-secondary bg-clip-text text-transparent">
									99.9%
								</div>
								<p className="text-muted-foreground">Uptime</p>
							</div>
						</div>
					</div>
				</section>

				{/* CTA Section */}
				<section className="py-24 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-12 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-4xl md:text-5xl font-bold mb-6">
									Join Us on Our Journey
								</h2>
								<p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
									Be part of the AI video revolution. Start
									creating with TabStudio today.
								</p>
								<Link href="/register" prefetch={false}>
									<Button
										variant="default"
										size="lg"
										className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
									>
										Get Started Free
									</Button>
								</Link>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}
